<template>
  <div class="campaign-table-wrapper">
    <v-table class="none-collapse-table campaign-list" style="width: 100%">
      <thead>
        <tr id="table-header" class="table-header-row">
          <th class="filler-column" />
          <th
            v-for="header in availableHeaders"
            :key="header.value || header.text"
            :class="[
              'column sortable',
              pagination.descending ? 'desc' : 'asc',
              header.sortable === false ? 'not-sortable' : 'is-sortable',
              header.value === pagination.sortBy ? 'active' : '',
            ]"
            @click="sortColumn(header.value, header.sortable)"
            data-testid="order-table-header"
            :style="
              (header.width ? `width: ${header.width};` : '') +
              (header.sortable === false ? 'cursor: default' : 'cursor: pointer')
            "
            :title="header.sortable === false ? '' : `Sort by ${header.text}`"
          >
            <div class="d-flex align-center">
              {{ header.text }}
              <v-icon
                size="small"
                v-if="header.sortable !== false"
                class="sort-icon"
                :class="{ 'sort-icon-active': header.value === pagination.sortBy }"
                :icon="getSortIcon(header)"
              />
            </div>
          </th>
          <th class="filler-column" />
        </tr>
        <tr class="bg-grey-lighten-4 table-filter-row">
          <th class="filler-column" />
          <th v-for="header in availableHeaders" :key="`${header.value || header.text}-filter`">
            <v-text-field
              v-if="filterHeaders.includes(header.text)"
              placeholder="Search"
              v-model="localFilters[header.value]"
              data-testid="order-table-filter"
              class="search-filter"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              prepend-inner-icon="search"
              @keyup="$emit('filter-changed', localFilters)"
            />
          </th>
          <th class="filler-column">
            <v-progress-circular
              v-show="loading"
              :indeterminate="true"
              size="25"
              width="2"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!loading && campaigns.length === 0">
          <td :colspan="availableHeaders.length + 2" class="text-center py-6">
            {{ emptyMessage }}
          </td>
        </tr>
        <!-- An ARCHIVED row (Archived view only) is not editable or viewable:
             clicking it does not open the editor. It offers Unarchive and
             Batches (the editor's restricted Batches-only mode). -->
        <tr
          v-for="item in campaigns"
          :key="item.id"
          @click="onRowClick(item)"
          :class="{ 'campaign-clickable-row': !item.archived_at }"
          :title="item.archived_at ? '' : 'Click row to open store'"
          data-testid="cl-row"
        >
          <td class="filler-column" />
          <td class="text-black top-v sales-rep-cell" v-if="showSalesRep">
            <p class="margin-b-0">{{ item.sales_reps }}</p>
          </td>
          <td class="top-v store-name">
            <span v-if="item.active || loading || item.archived_at">
              <p class="text-black store-name-text margin-b-0">{{ item.name }}</p>
            </span>
            <span v-else>
              <router-link
                :disabled="item.active || loading"
                :to="`/distributor/stores/${item.id}/edit`"
                style="text-decoration: none"
                data-testid="cl-new-edit-alt"
              >
                <p class="text-black store-name-text margin-b-0">{{ item.name }}</p>
              </router-link>
            </span>
            <button
              v-if="item.is_low_stock && !item.complete"
              type="button"
              class="store-low-stock-badge"
              data-testid="cl-low-stock"
              @click.stop="$emit('check-low-stock', item)"
            >
              <v-icon color="error" icon="error" size="14" />
              <span>Low stock</span>
            </button>
          </td>
          <td class="text-left top-v">
            <p class="text-black margin-b-0">{{ item.company_name }}</p>
          </td>
          <td class="text-left top-v">
            <!-- Per-row testid: a ui-runner selector cannot pick a row by
                 position, so a scenario reads ONE store's status here. -->
            <span :data-testid="`cl-row-status-${item.id}`">
              <status-chip :status="deriveStatus(item)" />
            </span>
          </td>
          <td class="text-left top-v">
            <span v-if="item.archived_at">{{ emDash }}</span>
            <p class="text-black margin-b-0" v-else-if="item.complete"><b>Complete</b></p>
            <div v-else>
              <!-- Readiness gates OPENING a store, never closing it: a live
                   store keeps its toggle while unready (gate round 1 on
                   #355), and the Missing Info note below still names why. -->
              <!-- Per-row wrapper testid: a ui-runner selector cannot pick a
                   row by position, so a scenario asks whether ONE store's
                   toggle is offered (cl-toggle-<id>). A wrapper, because a
                   parent testid on the v-switch would clobber cl-status. -->
              <div v-if="canToggle(item)" :data-testid="`cl-toggle-${item.id}`">
                <v-switch
                  class="switch-div"
                  density="compact"
                  hide-details
                  :model-value="toggleDisplayValue(item)"
                  @click.stop
                  @update:modelValue="onToggle(item, $event)"
                  data-testid="cl-status"
                  :disabled="interfaceLocked || loading || item.loading"
                />
              </div>
              <p v-if="!item.is_ready" :data-testid="`cl-missing-${item.id}`">
                <b>Missing Info</b>
                <template v-if="readinessCheckFailed(item.unready_tabs)">
                  <br /><span class="missing-detail">Readiness check failed</span>
                </template>
                <template v-else-if="unreadySections(item.unready_tabs).length">
                  <br />
                  <a
                    class="missing-detail missing-link"
                    href="#"
                    :data-testid="`cl-missing-link-${item.id}`"
                    @click.prevent.stop="goToSection(item, unreadySections(item.unready_tabs)[0].id)"
                  >{{ unreadySections(item.unready_tabs)[0].title }}</a>
                  <span
                    v-if="unreadySections(item.unready_tabs).length > 1"
                    class="missing-detail"
                  > +{{ unreadySections(item.unready_tabs).length - 1 }} more</span>
                </template>
                <template v-else-if="hasUnreadyReasons(item.unready_tabs)">
                  <!-- A reason with no editor section of its own: Review &
                       Launch lists every section's state. -->
                  <br />
                  <a
                    class="missing-detail missing-link"
                    href="#"
                    :data-testid="`cl-missing-link-${item.id}`"
                    @click.prevent.stop="goToEditor(item)"
                  >Review &amp; Launch</a>
                </template>
              </p>
            </div>
          </td>
          <td class="text-left top-v mode-cell" data-testid="cl-mode">
            {{ formatMode(item) }}
          </td>
          <td class="text-left top-v schedule-cell" data-testid="cl-schedule">
            <span
              :data-testid="`cl-row-schedule-${item.id}`"
              :title="scheduleTooltip(item)"
            >
              <template v-if="scheduleFor(item).tone === 'permanent' || scheduleFor(item).tone === 'empty'">
                {{ scheduleFor(item).label }}
              </template>
              <span
                v-else
                class="schedule-chip"
                :class="`schedule-chip--${scheduleFor(item).tone}`"
                :data-tone="scheduleFor(item).tone"
                data-testid="cl-schedule-chip"
              >
                {{ scheduleFor(item).label }}
              </span>
            </span>
          </td>
          <td class="text-left top-v">
            <!--
              TODO: multi-owner roster requires backend support. The
              /campaign/getCampaignList endpoint currently joins only
              the creator (created_by_id), not the full set of owners +
              collaborators attached to the campaign. See
              project_decorator-and-supplier-routing-model.md for the
              broader roster-of-people pattern this will eventually fold
              into.
            -->
            <avatar-stack :people="ownersFor(item)" :max="3" />
          </td>
          <td class="text-left top-v orders-cell" data-testid="cl-orders">
            <!--
              Placed-order count from getCampaignList (`orders_count`; 0 is
              a real count and shows as 0). The dash stays only for a row
              without the field. Revenue aggregation is still deferred.
            -->
            <span
              v-if="Number.isInteger(item.orders_count)"
              :data-testid="`cl-orders-count-${item.id}`"
            >{{ item.orders_count }}</span>
            <span v-else>{{ emDash }}</span>
          </td>
          <td class="text-left top-v actions-cell" data-testid="cl-actions">
            <v-btn
              v-if="item.archived_at"
              variant="outlined"
              size="small"
              rounded="pill"
              class="mr-2"
              :data-testid="`cl-batches-${item.id}`"
              @click.stop="$emit('open-batches', item)"
            >
              Batches
            </v-btn>
            <v-btn
              v-if="item.archived_at"
              variant="outlined"
              size="small"
              rounded="pill"
              :data-testid="`cl-unarchive-${item.id}`"
              :disabled="archiveDisabled(item)"
              @click.stop="onUnarchive(item)"
            >
              Unarchive
            </v-btn>
            <v-menu v-else location="bottom end" :close-on-content-click="true">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon
                  variant="plain"
                  size="small"
                  v-bind="props"
                  @click.stop
                  data-testid="cl-row-kebab"
                  :disabled="item.loading"
                  aria-label="Row actions"
                >
                  <v-icon icon="more_vert" />
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  data-testid="cl-edit"
                  @click.stop="goToEdit(item)"
                >
                  <v-list-item-title>
                    {{ loading || item.complete ? "View Orders and Reports" : "Edit" }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item
                  data-testid="cl-view-store"
                  :disabled="!storefrontUrl(item)"
                  @click.stop="onViewStore(item)"
                >
                  <v-list-item-title>View Store</v-list-item-title>
                </v-list-item>
                <v-list-item
                  data-testid="cl-duplicate"
                  :disabled="duplicateDisabled(item)"
                  @click.stop="onDuplicate(item)"
                >
                  <v-list-item-title>Duplicate</v-list-item-title>
                </v-list-item>
                <v-list-item
                  :data-testid="`cl-archive-${item.id}`"
                  :disabled="archiveDisabled(item)"
                  @click.stop="onArchive(item)"
                >
                  <v-list-item-title>Archive</v-list-item-title>
                </v-list-item>
                <v-list-item
                  data-testid="cl-delete"
                  :disabled="deleteDisabled(item)"
                  @click.stop="onDelete(item)"
                >
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-if="canChangeOwner"
                  data-testid="cl-change-owner"
                  @click.stop="$emit('change-owner', item)"
                >
                  <v-list-item-title>Change owner</v-list-item-title>
                </v-list-item>
                <v-list-item
                  data-testid="cl-complete"
                  :disabled="completeDisabled(item)"
                  @click.stop="onComplete(item)"
                >
                  <v-list-item-title>
                    {{ item.complete ? "Unlock" : "Complete" }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
          <td class="filler-column" />
        </tr>
      </tbody>
    </v-table>
    <table-pagination
      :page="pagination.page"
      :rows-per-page="pagination.rowsPerPage"
      :total-items="campaignCount"
      :rows-per-page-options="[25, 50, 100, 200]"
      @update:page="$emit('update:page', $event)"
      @update:rows-per-page="$emit('update:rows-per-page', $event)"
    />
    <v-spacer />
  </div>
</template>

<script>
import moment from "moment";
import TablePagination from "@/components/TablePagination.vue";
import StatusChip from "@/components/distributor/StatusChip.vue";
import AvatarStack from "@/components/distributor/AvatarStack.vue";
import { deriveStoreStatus } from "./storeStatus";
import { avatarKindForRoleSlug } from "./storeAccountRoles";
import { scheduleStatus } from "@/utils/storeSchedule";
import { windowMomentLabel } from "./storeWindow.js";
import { readinessCheckFailed, unreadySections } from "./unreadySections";

// Distributor design fidelity (handoff_2026-05-09/2026-05-29-distributor-account):
// the Stores list moves from the legacy 4-icon action tray to a single
// kebab menu, adopts a Status chip + Mode + Schedule + Owner avatar
// stack column set, and uses an em-dash placeholder for the Orders
// aggregate until the backend exposes counts. See
// design/distributor-sections.jsx ~lines 1833-2050 for the prototype.

export default {
  components: {
    "table-pagination": TablePagination,
    "status-chip": StatusChip,
    "avatar-stack": AvatarStack,
  },

  props: {
    campaigns: { type: Array, required: true },
    campaignCount: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    showSalesRep: { type: Boolean, default: false },
    campaignSwitchDisabled: { type: Boolean, default: false },
    interfaceLocked: { type: Boolean, default: false },
    subscriptionStatus: { type: String, default: null },
    pagination: { type: Object, required: true },
    completeButtonKey: { type: Number, default: 0 },
    stockButtonKey: { type: Number, default: 0 },
    // Owner-only: moving a store to another teammate (distributor.team.invite).
    canChangeOwner: { type: Boolean, default: false },
    // The empty-table line; the Archived view has its own.
    emptyMessage: { type: String, default: "You haven't built any stores yet." },
  },

  emits: [
    'open-campaign',
    'active-campaign',
    'duplicate-campaign',
    'delete-campaign',
    'archive-campaign',
    'open-batches',
    'unarchive-campaign',
    'change-owner',
    'change-completeness',
    'check-low-stock',
    'filter-changed',
    'update:page',
    'update:rows-per-page',
  ],

  data() {
    return {
      headers: [
        { text: "Store", title: "Store", value: "name", key: "name", width: "26%" },
        { text: "Client", title: "Client", value: "company_name", key: "company_name", width: "16%" },
        { text: "Status", title: "Status", value: "status", key: "status", sortable: false },
        { text: "Open/Closed", title: "Open/Closed", value: "active", key: "active", sortable: false },
        { text: "Mode", title: "Mode", value: "is_permanent_store", key: "is_permanent_store", sortable: false },
        { text: "Schedule", title: "Schedule", value: "end_date", key: "end_date" },
        { text: "Owner", title: "Owner", value: "first_name", key: "first_name", sortable: false },
        { text: "Orders", title: "Orders", value: "orders", key: "orders", sortable: false },
        { text: "", title: "", key: "actions", sortable: false },
      ],

      headers_salesrep: [
        { text: "Sales Reps", title: "Sales Reps", value: "sales_reps", key: "sales_reps", width: "180px" },
        { text: "Store", title: "Store", value: "name", key: "name", width: "22%" },
        { text: "Client", title: "Client", value: "company_name", key: "company_name", width: "14%" },
        { text: "Status", title: "Status", value: "status", key: "status", sortable: false },
        { text: "Open/Closed", title: "Open/Closed", value: "active", key: "active", sortable: false },
        { text: "Mode", title: "Mode", value: "is_permanent_store", key: "is_permanent_store", sortable: false },
        { text: "Schedule", title: "Schedule", value: "end_date", key: "end_date" },
        { text: "Owner", title: "Owner", value: "first_name", key: "first_name", sortable: false },
        { text: "Orders", title: "Orders", value: "orders", key: "orders", sortable: false },
        { text: "", title: "", key: "actions", sortable: false },
      ],

      filterHeaders: ["Store", "Client"],
      localFilters: [],
      // Em-dash for empty-cell placeholders. Kept as a data property so
      // the source file stays ASCII-only (per CLAUDE.md) and the literal
      // glyph lives only in the runtime string.
      emDash: "—",
    };
  },

  computed: {
    availableHeaders() {
      return this.showSalesRep ? this.headers_salesrep : this.headers;
    },
  },

  methods: {
    deriveStatus(item) {
      return deriveStoreStatus(item);
    },

    // The v-switch displays based on the DERIVED status, not the raw
    // ``item.active`` boolean. A pop-up store with active=true on the
    // server but end_date already passed reads as CLOSED (per the pill);
    // the toggle now matches. The store remains clickable to re-activate
    // via the existing activeCampaign() path, which auto-extends
    // end_date by 7 days when toggling from off -> on (see
    // StoreList.vue:790-793). Jake decision 2026-06-16.
    toggleDisplayValue(item) {
      return deriveStoreStatus(item) === "live";
    },

    // The v-switch is bound one-way (:model-value) so display can
    // diverge from the raw ``item.active``. We still need the legacy
    // parent handler to read the NEW intended value from item.active
    // (StoreList.vue activeCampaign reads ``if (campaign.active)``
    // straight off the row), so mirror what v-model did implicitly:
    // mutate the row, then emit. This keeps the duplicate-shopper +
    // end_date auto-extension behaviour intact.
    onToggle(item, newValue) {
      item.active = newValue;
      this.$emit("active-campaign", item);
    },

    formatMode(item) {
      if (item.is_permanent_store === true) return "Permanent";
      if (item.is_permanent_store === false) return "Pop-Up";
      return this.emDash;
    },

    scheduleFor(item) {
      // The zone the row's close is read in: /campaign/getCampaignList
      // resolves it server-side (distributor, platform default, UTC), the
      // same zone Store.has_closed uses -- so the chip agrees with the
      // status pill and with when the store actually closes.
      return scheduleStatus({
        end_date: item.end_date,
        is_permanent_store: item.is_permanent_store,
        now: new Date(),
        tz: item.schedule_timezone || undefined,
      });
    },

    // Hover text for the Schedule pill (card #585): the open and close
    // moments with their time and the zone they are read in -- the row's
    // schedule_timezone, already resolved server-side -- through the same
    // windowMomentLabel the editor header uses. Only a row that renders a
    // pill has one; Permanent and no-close rows show a bare dash.
    scheduleTooltip(item) {
      const tone = this.scheduleFor(item).tone;
      if (tone === "permanent" || tone === "empty") return undefined;
      const tz = item.schedule_timezone;
      const opens = windowMomentLabel(item.start_date, tz, {
        withDate: true,
        yearUnlessCurrent: true,
      });
      const closes = windowMomentLabel(item.end_date, tz, {
        withDate: true,
        yearUnlessCurrent: true,
      });
      if (opens && closes) return `Opens ${opens}; closes ${closes}`;
      if (closes) return `Closes ${closes}`;
      return undefined;
    },

    formatSchedule(item) {
      const start = item.start_date ? moment(item.start_date) : null;
      const end = item.end_date ? moment(item.end_date) : null;
      const startValid = start && start.isValid();
      const endValid = end && end.isValid();
      if (startValid && endValid) {
        // ASCII hyphen-minus in the source per CLAUDE.md; visually
        // the design's en-dash is close enough at this size.
        return `${start.format("MMM D")} - ${end.format("MMM D")}`;
      }
      if (endValid) return `Ends ${end.format("MMM D")}`;
      if (startValid) return `Starts ${start.format("MMM D")}`;
      return this.emDash;
    },

    ownersFor(item) {
      const first = (item.first_name || "").trim();
      const last = (item.last_name || "").trim();
      const fullName = `${first} ${last}`.trim();
      if (!fullName) return [];
      // Keyed on the creator's role SLUG, never its id. The previous
      // ``item.role_id === 1`` was wrong twice over: role 1 is Super
      // Admin on a seeded database (Owner is 2, and 9 on production),
      // and ``getCampaignList`` never emitted ``role_id`` at all -- so
      // every row, including real Owners, fell through to the builder
      // avatar on every database (#3580). Presentational only.
      const kind = avatarKindForRoleSlug(item.creator_role_slug);
      return [{ name: fullName, kind }];
    },

    duplicateDisabled(item) {
      return (
        this.loading ||
        this.subscriptionStatus === "suspended" ||
        this.subscriptionStatus === "canceled" ||
        !!item.loading
      );
    },

    deleteDisabled(item) {
      return (
        !!item.active ||
        !!item.complete ||
        this.loading ||
        this.subscriptionStatus === "suspended" ||
        this.subscriptionStatus === "canceled" ||
        !!item.loading
      );
    },

    completeDisabled(item) {
      return (
        this.interfaceLocked ||
        this.loading ||
        this.subscriptionStatus === "suspended" ||
        this.subscriptionStatus === "canceled" ||
        !!item.loading
      );
    },

    goToEdit(item) {
      this.$router.push("/distributor/stores/" + item.id + "/edit");
    },

    // Bug Backlog #355: "Missing Info" links to the section behind it.
    readinessCheckFailed,
    unreadySections,
    /**
     * The Open/Closed toggle shows for a store that is ready to open, and
     * for a LIVE store whatever its readiness, so it can always be closed.
     */
    canToggle(item) {
      if (this.campaignSwitchDisabled) return false;
      return !!item.is_ready || deriveStoreStatus(item) === "live";
    },

    hasUnreadyReasons(tabs) {
      return Array.isArray(tabs) && tabs.length > 0;
    },

    goToEditor(item) {
      this.$router.push("/distributor/stores/" + item.id + "/edit");
    },

    goToSection(item, sectionId) {
      this.$router.push(
        "/distributor/stores/" + item.id + "/edit/" + sectionId
      );
    },

    /**
     * Absolute public storefront URL for a row, or "" when it has no slug.
     *
     * Built the same way the rest of the editor builds it
     * (StoreEmailTemplates.storefrontUrl / StoreShopper.shopPrefix) and the
     * same way the backend's ``frontend_url("/shop/<slug>")`` does: a PATH on
     * the app host, not a ``<slug>.threadflow.store`` subdomain.
     *
     * BASE_URL is the build's PUBLIC PATH -- "/" in most builds -- so it is
     * only usable as a host when it actually carries a scheme; otherwise fall
     * back to the current origin. Returning "" (rather than a broken URL)
     * lets the menu item disable itself for a store that has no slug yet.
     */
    storefrontUrl(item) {
      const slug = item && item.slug;
      if (!slug) return "";
      const configured = process.env.BASE_URL || "";
      const base = (
        /^https?:\/\//i.test(configured)
          ? configured
          : window.location.origin || ""
      ).replace(/\/+$/, "");
      if (!base) return "";
      return `${base}/shop/${String(slug).toLowerCase()}`;
    },

    onViewStore(item) {
      const url = this.storefrontUrl(item);
      if (!url) return;
      // New tab: the distributor is mid-task in the editor list, and the
      // storefront is a different audience's surface -- navigating away in
      // place would lose their place in the list. noopener/noreferrer because
      // this is a user-content-derived URL opened with target=_blank.
      window.open(url, "_blank", "noopener,noreferrer");
    },

    onDuplicate(item) {
      if (this.duplicateDisabled(item)) return;
      this.$emit("duplicate-campaign", item.id);
    },

    onDelete(item) {
      if (this.deleteDisabled(item)) return;
      this.$emit("delete-campaign", item);
    },

    onRowClick(item) {
      if (item.archived_at) return;
      this.$emit("open-campaign", item);
    },

    // Archive / Unarchive share delete's subscription gating. Unlike delete,
    // archive is offered for a live store too: the server switches it off,
    // and refuses (409) while any of its orders is still open.
    archiveDisabled(item) {
      return (
        this.loading ||
        this.subscriptionStatus === "suspended" ||
        this.subscriptionStatus === "canceled" ||
        !!item.loading
      );
    },

    onArchive(item) {
      if (this.archiveDisabled(item)) return;
      this.$emit("archive-campaign", item);
    },

    onUnarchive(item) {
      if (this.archiveDisabled(item)) return;
      this.$emit("unarchive-campaign", item);
    },

    onComplete(item) {
      if (this.completeDisabled(item)) return;
      this.$emit("change-completeness", item);
    },

    sortColumn(column, sortable) {
      if (sortable != false) {
        if (this.pagination.sortBy === column) {
          this.$emit('update:page', this.pagination.page); // trigger reactivity
          this.pagination.descending = !this.pagination.descending;
        } else {
          this.pagination.sortBy = column;
          this.pagination.descending = false;
        }
      }
    },

    getSortIcon(header) {
      if (header.sortable === false) {
        return "";
      }

      if (this.pagination.sortBy !== header.value) {
        return "unfold_more";
      }

      return this.pagination.descending ? "arrow_downward" : "arrow_upward";
    },
  },
};
</script>

<style scoped>
.missing-detail {
  font-size: 12px;
}
.missing-link {
  text-decoration: underline;
  cursor: pointer;
}
/* The store name opens the store (the row carries @click="open-campaign"),
   but nothing said so: no underline, no pointer, no hover change. A
   distributor had no way to know the name was actionable, so the row's
   primary action was effectively hidden.

   Deliberately a HOVER-only underline rather than a permanently-underlined
   link: every row in the table would be underlined otherwise, which reads as
   visual noise in a dense list. The pointer cursor is the always-on signal;
   the underline confirms it on approach. */
.store-name-text {
  font-weight: 500;
  cursor: pointer;
}

.store-name-text:hover {
  text-decoration: underline;
}

.store-low-stock-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 0;
  background: none;
  border: none;
  color: #d32f2f;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
}
.store-low-stock-badge:hover {
  text-decoration: underline;
}

.mode-cell,
.schedule-cell {
  font-size: 12px;
  color: var(--tf-fg-2);
}

/* Schedule chip — mirrors StatusChip's semantic token mapping:
 *   red    -> --tf-error-soft / --tf-red       (ended)
 *   yellow -> --tf-warning-soft / --tf-warning-dark  (ending soon)
 *   green  -> --tf-success-soft / --tf-success  (ending later)
 * Permanent + empty render as plain text (no chip), per Jake's spec.
 */
.schedule-chip {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 3px 9px;
  border-radius: 9999px;
  line-height: 1.4;
}
.schedule-chip--red {
  background: var(--tf-error-soft);
  color: var(--tf-red);
}
.schedule-chip--yellow {
  background: var(--tf-warning-soft);
  color: var(--tf-warning-dark);
}
.schedule-chip--green {
  background: var(--tf-success-soft);
  color: var(--tf-success);
}

.orders-cell {
  text-align: right;
  font-family: "Lucida Console", monospace;
  font-size: 13px;
  color: var(--tf-fg-2);
}

.actions-cell {
  text-align: right;
  white-space: nowrap;
}
</style>
